<script lang="ts">
	import { onDestroy } from 'svelte'
	import type { AuthUser } from '../lib/auth'
	import type { Game } from '../lib/catalog'
	import { BALANCE_PRESETS, ENVIRONMENT } from '../lib/config'
	import { balanceAmount, type Session } from '../lib/launch'
	import { newPlayerId } from '../lib/player'

	let {
		session,
		game,
		url,
		user,
		currencies,
		languages,
		onlaunch,
		onlogout,
	}: {
		session: Session
		game: Game
		url: string
		user: AuthUser
		currencies: readonly string[]
		languages: ReadonlyArray<{ readonly code: string; readonly label: string }>
		onlaunch: () => void
		onlogout: () => void
	} = $props()

	type CopyState = 'idle' | 'ok' | 'fail'

	const COPY_LABELS: Record<CopyState, string> = {
		idle: 'COPY URL',
		ok: 'COPIED ✓',
		fail: 'COPY FAILED',
	}

	let copyState = $state<CopyState>('idle')
	let resetTimer: ReturnType<typeof setTimeout> | undefined

	// The clipboard is unavailable on an insecure origin, and it rejects when the
	// page is not focused. Reporting the failure beats a green tick over an
	// empty clipboard — the URL is the one thing a tester is here to take away.
	async function copy(): Promise<void> {
		try {
			await navigator.clipboard.writeText(url)
			copyState = 'ok'
		} catch {
			copyState = 'fail'
		}

		clearTimeout(resetTimer)
		resetTimer = setTimeout(() => (copyState = 'idle'), 1400)
	}

	onDestroy(() => clearTimeout(resetTimer))

	function applyPreset(value: string): void {
		session.balance = value
	}

	/*
		A prototype takes no session: there is no RGS behind it to hold a wallet,
		so player, balance, currency and language would all be settings that reach
		nothing. The panel drops to what is actually true of one — which game, and
		the button that opens it.
	*/
	const prototype = $derived(game.type === 'prototype')

	const summary = $derived(
		prototype
			? game.studio
			: [
					game.studio,
					`${balanceAmount(session.balance).toLocaleString('en-US')} ${session.currency}`,
					session.language,
				].join(' · '),
	)
</script>

<aside>
	<header>
		<span class="mark"></span>
		<span class="brand">REELDESK</span>
		<span class="env">v{__VERSION__} · {ENVIRONMENT}</span>
	</header>

	<div class="config">
		{#if prototype}
			<h2>PROTOTYPE</h2>
			<div class="selected">
				<span class="selected-name">{game.name}</span>
				<span class="selected-sub">{summary}</span>
			</div>
			<p class="note">no session — the build opens at its own URL</p>
		{:else}
			<h2>SESSION CONFIG</h2>

			<div class="field">
				<label for="player">player</label>
				<div class="row">
					<input
						id="player"
						class="uuid"
						bind:value={session.player}
						title={session.player}
						spellcheck="false"
						autocomplete="off"
					/>
					<!--
						The uuid is the account on this RGS, so a fresh one is a fresh
						wallet — and the only way past a round the client cannot finish.
					-->
					<button
						class="ghost regen"
						type="button"
						onclick={() => (session.player = newPlayerId())}
						title="mint a new player at full balance"
						aria-label="Mint a new player"
					>
						⟳
					</button>
				</div>
			</div>

			<div class="pair">
				<div class="field">
					<label for="balance">balance</label>
					<input id="balance" bind:value={session.balance} inputmode="decimal" autocomplete="off" />
				</div>
				<div class="field">
					<label for="currency">currency</label>
					<select id="currency" bind:value={session.currency}>
						{#each currencies as code (code)}
							<option value={code}>{code}</option>
						{/each}
					</select>
				</div>
			</div>

			<div class="presets">
				{#each BALANCE_PRESETS as preset (preset.value)}
					<button class="ghost" type="button" onclick={() => applyPreset(preset.value)}>
						{preset.label}
					</button>
				{/each}
			</div>

			<div class="field">
				<label for="language">language</label>
				<select id="language" bind:value={session.language}>
					{#each languages as language (language.code)}
						<option value={language.code}>{language.code} — {language.label}</option>
					{/each}
				</select>
			</div>

			<div class="divider"></div>

			<h2>SELECTED GAME</h2>
			<div class="selected">
				<span class="selected-name">{game.name}</span>
				<span class="selected-sub">{summary}</span>
			</div>

			<h2>LAUNCH URL</h2>
			<p class="url">{url}</p>
			<button class="ghost copy" type="button" onclick={copy}>{COPY_LABELS[copyState]}</button>
		{/if}
	</div>

	<footer>
		<button class="launch" type="button" onclick={onlaunch}>
			LAUNCH {prototype ? 'PROTOTYPE' : 'GAME'} ↗
		</button>
		<div class="account">
			<span class="who" title={user.email}>{user.email}</span>
			{#if user.role === 'admin'}
				<a class="bo" href="/backoffice/">BACK OFFICE ↗</a>
			{/if}
			<button class="ghost out" type="button" onclick={onlogout}>SIGN OUT</button>
		</div>
	</footer>
</aside>

<style>
	aside {
		display: flex;
		flex-direction: column;
		flex: none;
		width: 328px;
		background: var(--panel);
		border-right: 1px solid var(--line);
	}

	header {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 20px 22px;
		border-bottom: 1px solid var(--line);
	}

	.mark {
		width: 10px;
		height: 10px;
		border-radius: 2px;
		background: var(--accent);
	}

	.brand {
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 0.06em;
	}

	.env {
		margin-left: auto;
		color: var(--dim);
		font-size: 10px;
		letter-spacing: 0.08em;
	}

	.config {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 18px;
		padding: 22px;
		overflow: auto;
	}

	h2 {
		margin: 0;
		color: var(--dim);
		font-size: 10px;
		font-weight: 400;
		letter-spacing: 0.16em;
	}

	.field {
		display: flex;
		flex-direction: column;
		gap: 7px;
		min-width: 0;
	}

	.field label {
		color: var(--muted);
		font-size: 11px;
	}

	input,
	select {
		width: 100%;
		min-width: 0;
		border: 1px solid var(--line-strong);
		border-radius: var(--radius);
		background: var(--bg);
		color: var(--text);
		font-size: 13px;
	}

	input {
		padding: 9px 11px;
	}

	select {
		padding: 9px 8px;
	}

	/* A uuid is 36 characters in a 284px column: it scrolls, so drop a size. */
	.uuid {
		font-size: 12px;
	}

	.row {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 34px;
		gap: 8px;
	}

	.regen {
		padding: 0;
		font-size: 14px;
		line-height: 1;
	}

	/* The currency column is fixed so the balance keeps the elastic half. */
	.pair {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 108px;
		gap: 10px;
	}

	.presets {
		display: flex;
		gap: 8px;
	}

	.presets .ghost {
		flex: 1;
		padding: 6px 0;
	}

	.ghost {
		border: 1px solid var(--line-strong);
		border-radius: 5px;
		background: var(--btn);
		color: var(--muted);
		font-size: 11px;
		cursor: pointer;
	}

	.ghost:hover {
		border-color: var(--accent);
		color: var(--text);
	}

	.divider {
		height: 1px;
		background: var(--line);
	}

	.selected {
		display: flex;
		flex-direction: column;
		gap: 5px;
		padding: 12px;
		border: 1px solid var(--line-strong);
		border-radius: var(--radius);
		background: var(--bg);
	}

	.selected-name {
		font-size: 13px;
		font-weight: 700;
	}

	.selected-sub {
		color: var(--dim);
		font-size: 11px;
	}

	.note {
		margin: 0;
		color: var(--faint);
		font-size: 11px;
		line-height: 1.6;
	}

	.url {
		margin: 0;
		padding: 11px 12px;
		border: 1px solid var(--line-strong);
		border-radius: var(--radius);
		background: var(--bg);
		color: var(--muted);
		font-size: 11px;
		line-height: 1.65;
		word-break: break-all;
	}

	.copy {
		padding: 9px 0;
		border-radius: var(--radius);
		letter-spacing: 0.08em;
	}

	footer {
		padding: 18px 22px;
		border-top: 1px solid var(--line);
	}

	.launch {
		width: 100%;
		padding: 13px 0;
		border: none;
		border-radius: var(--radius);
		background: var(--accent);
		color: var(--on-accent);
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.12em;
		cursor: pointer;
	}

	.launch:hover {
		background: var(--accent-hover);
	}

	.account {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 12px;
	}

	.who {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: var(--dim);
		font-size: 10px;
	}

	.bo {
		flex: none;
		color: var(--accent);
		font-size: 10px;
		letter-spacing: 0.06em;
		text-decoration: none;
	}

	.bo:hover {
		color: var(--accent-hover);
	}

	.out {
		flex: none;
		padding: 5px 8px;
		font-size: 10px;
		letter-spacing: 0.06em;
	}
</style>
